import { TaskPropertyButton } from './task-property.button';
import { format } from 'date-fns';
import { dateShort } from '../../../assets/contants/dates-fns';
import { CalendarIcon } from '@radix-ui/react-icons';
import React from 'react';
import { openModal } from '../../../core/modal/open-modal';
import { DateSelectionModal } from '../../process/components/date-selection.modal';
import {
  asPlainDate,
  fromLocalDate,
  isPlainDate,
  toLocalDate,
  todayPlainDate,
  type PlainDate,
} from '../../../core/services/plain-date';

/**
 * A `Task` due date is an instant, but a bare `YYYY-MM-DD` can still reach here — a stale cached
 * payload, or a seed taken from a step. `new Date('2026-08-25')` is UTC midnight and reads back
 * as Aug 24 for every viewer west of UTC, so a calendar day goes through `toLocalDate` instead.
 */
const asLocalDate = (raw: Date | string): Date =>
  isPlainDate(raw) ? toLocalDate(raw) : new Date(raw);

interface CreateTaskDatePropertyProps {
  /** A legacy instant string or `Date`: `Task.dueDate` is not migrated to `PlainDate`. */
  value?: Date | string | null;
  /**
   * The calendar day the producer picked, or `null` when they cleared it. `Task`'s API path
   * cannot take a bare `YYYY-MM-DD`, so callers must send it back through `toLegacyInstant`.
   */
  onChange: (value: PlainDate | null) => void;
}

export const CreateTaskDateProperty: React.FC<CreateTaskDatePropertyProps> = ({
  value,
  onChange,
}) => {
  // The modal hands back a raw `<input type="date">` value: a real calendar day, or `null`/`''`
  // when the native clear icon was used. This is the boundary where the `PlainDate` is minted;
  // a non-empty but malformed value has no safe day to mint and is dropped in place.
  const handleSubmit = async (date: string | null) => {
    if (!date) {
      onChange(null);
      return;
    }

    if (isPlainDate(date)) {
      onChange(asPlainDate(date));
    }
  };

  // `DateSelectionModal` renders its `value` straight into `<input type="date">`, so it has to be
  // a `YYYY-MM-DD` day — a `Date` or a `'Aug 25, 2026'` label leaves the field blank. Reading the
  // local day off the instant is what the label below already does. No value still means today,
  // as it did before.
  const openEditModal = () => {
    openModal(DateSelectionModal, {
      fieldName: 'dueDate',
      value: value ? fromLocalDate(asLocalDate(value)) : todayPlainDate(),
      onSubmit: handleSubmit,
    });
  };

  return (
    <TaskPropertyButton
      icon={<CalendarIcon />}
      label={value ? format(asLocalDate(value), dateShort) : 'Set due date'}
      onClick={openEditModal}
      isActive={!!value}
    />
  );
};
